import { useState, useCallback, useEffect, useRef } from 'react';
import { SNAP_ORIGIN as BUILD_SNAP_ORIGIN, SNAP_VERSION as BUILD_SNAP_VERSION } from '../lib/snapConfig.js';
import { trackEvent } from '../lib/analytics.js';
import { trackConversion } from '../services/conversionTracking.js';
import { notifySnapInstalled, restoreSubscription } from '../services/api.js';
import { getRuntimeConfig } from '../services/runtimeConfig.js';

interface SnapState {
  isMetaMask: boolean;
  isInstalled: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useSnap(authToken?: string | null) {
  const [state, setState] = useState<SnapState>({
    isMetaMask: false,
    isInstalled: false,
    isLoading: true,
    error: null,
  });

  // Runtime snap config (loaded async, falls back to build-time constants)
  const snapOriginRef = useRef<string>(BUILD_SNAP_ORIGIN);
  const snapVersionRef = useRef<string | undefined>(BUILD_SNAP_VERSION);

  const detectSnap = useCallback(async () => {
    // Try to get runtime config first (self-healing if VITE_SNAP_ID missing)
    try {
      const config = await getRuntimeConfig();
      if (config.snapId) {
        snapOriginRef.current = config.snapId;
        snapVersionRef.current = config.snapVersion || undefined;
      }
    } catch {
      // Use build-time fallback (already set in refs)
    }

    const snapOrigin = snapOriginRef.current;

    if (!window.ethereum) {
      setState({ isMetaMask: false, isInstalled: false, isLoading: false, error: null });
      return;
    }

    if (!snapOrigin) {
      setState({ isMetaMask: true, isInstalled: false, isLoading: false, error: 'Snap ID not configured. Please contact support.' });
      return;
    }

    try {
      const snaps = (await window.ethereum.request({
        method: 'wallet_getSnaps',
      })) as Record<string, unknown> | null;

      const installed = snaps
        ? Object.keys(snaps).some((key) => key === snapOrigin || key.startsWith(`${snapOrigin}@`))
        : false;
      setState({ isMetaMask: true, isInstalled: installed, isLoading: false, error: null });
    } catch {
      setState({ isMetaMask: true, isInstalled: false, isLoading: false, error: null });
    }
  }, []);

  useEffect(() => {
    detectSnap();
  }, [detectSnap]);

  const installSnap = useCallback(async () => {
    const snapOrigin = snapOriginRef.current;
    const snapVersion = snapVersionRef.current;

    if (!snapOrigin) {
      setState((s) => ({ ...s, error: 'Snap ID not configured.' }));
      return;
    }

    if (!window.ethereum) {
      setState((s) => ({ ...s, error: 'MetaMask is not installed.' }));
      return;
    }

    trackEvent('click_install_snap');
    trackConversion('snap_install_started');
    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      await window.ethereum.request({
        method: 'wallet_requestSnaps',
        params: {
          [snapOrigin]: snapVersion ? { version: snapVersion } : {},
        },
      });

      const snaps = (await window.ethereum.request({
        method: 'wallet_getSnaps',
      })) as Record<string, unknown> | null;

      const confirmed = snaps
        ? Object.keys(snaps).some((key) => key === snapOrigin || key.startsWith(`${snapOrigin}@`))
        : false;

      if (confirmed) {
        trackEvent('snap_installed');
        trackConversion('snap_installed');
        setState({ isMetaMask: true, isInstalled: true, isLoading: false, error: null });

        if (authToken) {
          notifySnapInstalled(authToken, snapOrigin, snapVersion ?? 'latest').catch(() => {});
          restoreSubscription(authToken).catch(() => {});
        }
      } else {
        setState({ isMetaMask: true, isInstalled: false, isLoading: false, error: 'Snap installation could not be verified.' });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to install Snap';
      setState((s) => ({ ...s, isLoading: false, error: message }));
    }
  }, [authToken]);

  return { ...state, installSnap, detectSnap };
}
